"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState, type RefObject } from "react";
import Link from "next/link";
import { AI_MONTHLY_PLAN, formatCredits, type AiMonthlyPlan } from "@/lib/ai-credits/monthly-policy";
import { loadStripe } from "@stripe/stripe-js";
import { CheckoutProvider, PaymentElement, useCheckout, type StripeCheckoutValue } from "@stripe/react-stripe-js/checkout";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTheme } from "@/components/theme/theme-provider";
import { getStripeAppearance } from "@/lib/stripe-appearance";
import { nextMonthlyBillingDate, billingDayLabel } from "@/lib/ai-credits/billing-schedule";
import { confirmedPaymentFailure, PAYMENT_STATUS_UNKNOWN } from "@/lib/payments/payment-failure";
const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!);
export function AiMonthlyCheckout(t0) {
  const $ = _c(29);
  const {
    clientSecret,
    onClose,
    onComplete,
    email: t1,
    returnFocusRef,
    plan: t2
  } = t0;
  const email = t1 === undefined ? "" : t1;
  const plan = t2 === undefined ? AI_MONTHLY_PLAN : t2;
  const {
    resolvedTheme
  } = useTheme();
  let t3;
  if ($[0] !== resolvedTheme) {
    t3 = getStripeAppearance(resolvedTheme);
    $[0] = resolvedTheme;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t3) {
    t4 = {
      appearance: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== clientSecret || $[5] !== t4) {
    t5 = {
      clientSecret,
      elementsOptions: t4
    };
    $[4] = clientSecret;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const options = t5;
  let t6;
  if ($[7] !== onClose) {
    t6 = open => {
      if (!open) {
        onClose();
      }
    };
    $[7] = onClose;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== plan.name) {
    t7 = <DialogTitle>Subscribe to {plan.name}</DialogTitle>;
    $[9] = plan.name;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== plan.units) {
    t8 = formatCredits(plan.units);
    $[11] = plan.units;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8) {
    t9 = <DialogDescription>{t8} paid credits/month</DialogDescription>;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== email || $[16] !== onComplete) {
    t10 = <MonthlyPayment onComplete={onComplete} email={email} />;
    $[15] = email;
    $[16] = onComplete;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== options || $[19] !== t10) {
    t11 = <CheckoutProvider stripe={stripePromise} options={options}>{t10}</CheckoutProvider>;
    $[18] = options;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== returnFocusRef || $[22] !== t11 || $[23] !== t7 || $[24] !== t9) {
    t12 = <DialogContent returnFocusRef={returnFocusRef} className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">{t7}{t9}{t11}</DialogContent>;
    $[21] = returnFocusRef;
    $[22] = t11;
    $[23] = t7;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t6) {
    t13 = <Dialog open={true} onOpenChange={t6}>{t12}</Dialog>;
    $[26] = t12;
    $[27] = t6;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
}
function MonthlyPayment(t0) {
  const $ = _c(7);
  const {
    onComplete,
    email
  } = t0;
  const result = useCheckout();
  if (result.type === "loading") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p role="status">Loading payment form…</p>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (result.type === "error") {
    let t1;
    if ($[1] !== result.error.message) {
      t1 = <p role="alert" className="text-destructive">{result.error.message}</p>;
      $[1] = result.error.message;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== email || $[4] !== onComplete || $[5] !== result.checkout) {
    t1 = <MonthlyPaymentForm checkout={result.checkout} onComplete={onComplete} initialEmail={email} />;
    $[3] = email;
    $[4] = onComplete;
    $[5] = result.checkout;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}
export function MonthlyPaymentForm({
  checkout,
  onComplete,
  initialEmail = ""
}: {
  checkout: StripeCheckoutValue;
  onComplete: () => Promise<void>;
  initialEmail?: string;
}) {
  const [email, setEmail] = useState(checkout.email || initialEmail);
  const [pending, setPending] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const now = new Date();
  const anchor = checkout.recurring?.dueNext.billingCycleAnchor;
  const renewal = anchor ? new Date(anchor * 1000) : nextMonthlyBillingDate(now);
  const formatMoney = (cents: number) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: checkout.currency
  }).format(cents / checkout.minorUnitsAmountDivisor);
  const todayAmount = formatMoney(checkout.total.total.minorUnitsAmount);
  const recurringAmount = checkout.recurring ? formatMoney(checkout.recurring.dueNext.total.minorUnitsAmount) : null;
  const renewalDate = renewal.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
  const billingDay = billingDayLabel(now.getDate());
  const confirm = async (event: React.FormEvent) => {
    event.preventDefault();
    if (pending || paid || uncertain) return;
    setPending(true);
    setError(null);
    try {
      // The server sets return_url on the Checkout Session; Stripe rejects a second URL here.
      const result = await checkout.confirm({
        email,
        redirect: "if_required"
      });
      if (result.type === "error") {
        setError(result.error.code === "paymentFailed" ? confirmedPaymentFailure(result.error.message) : result.error.message);
        return;
      }
      setPaid(true);
      await onComplete();
    } catch {
      setUncertain(true);
      setError(PAYMENT_STATUS_UNKNOWN);
    } finally {
      setPending(false);
    }
  };
  return <form onSubmit={confirm} className="space-y-5">
        <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2 text-sm">
            <p className="flex justify-between gap-4"><span>Due today</span><strong>{todayAmount}</strong></p>
            {recurringAmount && <>
                <p className="flex justify-between gap-4"><span>Monthly renewal</span><strong>{recurringAmount}/month</strong></p>
                <p>Every month on the {billingDay}, starting {renewalDate}.</p>
                <p className="text-muted-foreground">Cancel anytime before renewal.</p>
                {now.getDate() > 28 && <p className="text-muted-foreground">Shorter months renew on the last day.</p>}
            </>}
        </div>
        <div className="space-y-2">
            <label htmlFor="ai-monthly-email" className="text-sm font-medium">Email</label>
            <Input id="ai-monthly-email" type="email" autoComplete="email" required value={email} onChange={event_0 => setEmail(event_0.target.value)} disabled={pending || paid} />
        </div>
        <PaymentElement options={{
      layout: {
        type: "accordion",
        radios: "never",
        defaultCollapsed: false
      },
      wallets: {
        link: "never"
      }
    }} onLoadError={() => {
      setLoadFailed(true);
      setError("Couldn’t load payment form. Close and try again.");
    }} />
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        {paid && <p role="status" className="text-sm">Payment submitted.</p>}
        <Button className="w-full" type="submit" disabled={pending || paid || uncertain || loadFailed || !recurringAmount}>
            {pending ? "Confirming…" : paid ? "Payment submitted" : `Pay ${todayAmount} today & subscribe`}
        </Button>
        <p className="text-xs text-muted-foreground">By subscribing, you agree to these monthly charges and our <Link href="/terms" className="underline">Terms of Service</Link>.</p>
    </form>;
}